import { CalendarDays, Clock, Globe, Link2, MapPin, Repeat, Users, Video, X } from "lucide-react";
import { Component, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import type {
  CalendarEvent,
  CalendarOccurrence,
  CalendarParticipant,
  CalendarShellSnapshot,
} from "../core/shell-types";
import {
  calendarById,
  calendarColor,
  descriptionText,
  differingZone,
  eventStatusLabel,
  freeBusyLabel,
  occurrenceColor,
  occurrenceWhenLabel,
  participationStatusLabel,
  safeHref,
} from "./format";
import { Sheet } from "./Sheet";

function Row({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Clock;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 text-sm">
        <span className="sr-only">{label}: </span>
        {children}
      </div>
    </div>
  );
}

function statusBadgeClass(status: string) {
  switch (status) {
    case "accepted":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
    case "declined":
      return "bg-red-500/15 text-red-700 dark:text-red-300";
    case "tentative":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
    default:
      return "bg-muted text-muted-foreground";
  }
}

// A render throw while formatting one event (a malformed server payload the
// helpers did not anticipate) must not unmount the whole shell: App.tsx has
// no boundary above this. Keyed by the occurrence so a new selection retries.
class DetailBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.warn("Lately: event detail failed to render", error);
  }

  render() {
    if (this.state.failed) {
      return (
        <p role="alert" data-event-detail-error className="px-4 py-4 text-sm text-muted-foreground">
          Couldn't show this event.
        </p>
      );
    }
    return this.props.children;
  }
}

function participantList(event: CalendarEvent): CalendarParticipant[] {
  const list = Object.values(event.participants ?? {});
  return list.sort((a, b) => {
    const ownerA = a.roles?.owner ? 0 : 1;
    const ownerB = b.roles?.owner ? 0 : 1;
    if (ownerA !== ownerB) {
      return ownerA - ownerB;
    }
    return (a.name || a.email || "").localeCompare(b.name || b.email || "");
  });
}

function DetailBody({
  snapshot,
  occurrence,
  event,
}: {
  snapshot: CalendarShellSnapshot;
  occurrence: CalendarOccurrence | undefined;
  event: CalendarEvent | undefined;
}) {
  const calendarId = occurrence?.calendarId ?? Object.keys(event?.calendarIds ?? {})[0] ?? "";
  const calendar = calendarById(snapshot, calendarId);
  const color = occurrence
    ? occurrenceColor(snapshot, occurrence)
    : calendarColor(snapshot, calendarId);
  const zone = occurrence ? differingZone(occurrence, snapshot.timeZone) : null;
  const status = occurrence?.status ?? event?.status ?? "confirmed";
  const freeBusy = occurrence?.freeBusyStatus ?? event?.freeBusyStatus ?? "busy";
  const locations = Object.values(event?.locations ?? {});
  const virtualLocations = Object.values(event?.virtualLocations ?? {});
  const links = Object.values(event?.links ?? {});
  const participants = event ? participantList(event) : [];
  const description = event ? descriptionText(event) : "";

  return (
    <div className="flex flex-col gap-4 px-4 py-4">
      <div className="flex gap-3">
        <span
          aria-hidden
          className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
          style={{ background: color }}
        />
        <div className="min-w-0 flex-1">
          <h3
            className={cn(
              "font-display text-lg leading-snug tracking-tight",
              status === "cancelled" && "line-through",
            )}
          >
            {occurrence?.title || event?.title || "(untitled)"}
          </h3>
          {occurrence?.isDraft || event?.isDraft ? (
            <span className="text-xs text-muted-foreground">Draft</span>
          ) : null}
        </div>
      </div>

      {occurrence ? (
        <Row icon={Clock} label="When">
          <span data-event-when>{occurrenceWhenLabel(occurrence, snapshot.timeZone)}</span>
          {occurrence.isRecurring ? (
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Repeat aria-hidden className="h-3 w-3" />
              Repeats
            </span>
          ) : null}
          {zone ? (
            <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <Globe aria-hidden className="h-3 w-3" />
              Event time zone: {zone}
            </span>
          ) : null}
        </Row>
      ) : null}

      <Row icon={CalendarDays} label="Calendar">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: color }} />
          {calendar?.name ?? "Calendar"}
        </span>
        <span className="ml-2 text-xs text-muted-foreground">
          {eventStatusLabel(status)} · {freeBusyLabel(freeBusy)}
        </span>
      </Row>

      {locations.length > 0 || (occurrence?.locationSummary && virtualLocations.length === 0) ? (
        <Row icon={MapPin} label="Location">
          {locations.length > 0 ? (
            <ul className="space-y-1">
              {locations.map((location, position) => (
                <li key={`${location.name}:${position}`}>
                  <span>{location.name}</span>
                  {location.description ? (
                    <span className="block text-xs text-muted-foreground">
                      {location.description}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <span>{occurrence?.locationSummary}</span>
          )}
        </Row>
      ) : null}

      {virtualLocations.length > 0 ? (
        <Row icon={Video} label="Join">
          <ul className="space-y-1">
            {virtualLocations.map((location, position) => {
              const href = safeHref(location.uri);
              const label = location.name || location.uri;
              return (
                <li key={`${location.uri}:${position}`} className="min-w-0">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block truncate text-primary underline-offset-2 hover:underline"
                    >
                      {label}
                    </a>
                  ) : (
                    <span className="block truncate">{label}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </Row>
      ) : null}

      {links.length > 0 ? (
        <Row icon={Link2} label="Links">
          <ul className="space-y-1">
            {links.map((link, position) => {
              const href = safeHref(link.href);
              const label = link.title || link.href;
              return (
                <li key={`${link.href}:${position}`} className="min-w-0">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block truncate text-primary underline-offset-2 hover:underline"
                    >
                      {label}
                    </a>
                  ) : (
                    <span className="block truncate">{label}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </Row>
      ) : null}

      {participants.length > 0 ? (
        <Row icon={Users} label="Participants">
          <ul className="space-y-1.5">
            {participants.map((participant, position) => {
              const label = participant.name || participant.email || "Participant";
              return (
                <li
                  key={`${participant.email ?? participant.name}:${position}`}
                  className="flex min-w-0 items-center gap-2"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{label}</span>
                    {participant.email && participant.name ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {participant.email}
                      </span>
                    ) : null}
                  </span>
                  {participant.roles?.owner ? (
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Organizer
                    </span>
                  ) : null}
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium",
                      statusBadgeClass(participant.participationStatus),
                    )}
                  >
                    {participationStatusLabel(participant.participationStatus)}
                  </span>
                </li>
              );
            })}
          </ul>
        </Row>
      ) : null}

      {description ? (
        <div className="border-t border-border/60 pt-3">
          <p
            className="whitespace-pre-wrap break-words text-sm leading-relaxed"
            data-event-description
          >
            {description}
          </p>
        </div>
      ) : null}

      {!event && !occurrence ? (
        <p className="text-sm text-muted-foreground">Loading event…</p>
      ) : null}
    </div>
  );
}

// The detail surface for the selected occurrence: a right-hand panel on
// desktop, a full-height sheet on compact widths. Closing clears the
// selection in the core (calendarSelectOccurrence null).
export function EventDetail({
  snapshot,
  compact,
  onClose,
}: {
  snapshot: CalendarShellSnapshot;
  compact: boolean;
  onClose: () => void;
}) {
  const key = snapshot.selectedOccurrenceKey ?? null;
  if (!key) {
    return null;
  }
  const occurrence = snapshot.occurrences.find((candidate) => candidate.key === key);
  const event = snapshot.selectedEvent ?? undefined;
  const title = occurrence?.title || event?.title || "Event";

  if (compact) {
    return (
      <Sheet title="Event" variant="full" onClose={onClose} label={title} testId="event">
        <div data-event-detail data-occurrence-key={key}>
          <DetailBoundary key={key}>
            <DetailBody snapshot={snapshot} occurrence={occurrence} event={event} />
          </DetailBoundary>
        </div>
      </Sheet>
    );
  }

  return (
    <aside
      aria-label="Event details"
      data-event-detail
      data-occurrence-key={key}
      className="flex w-80 shrink-0 flex-col border-l border-border/70"
    >
      <div className="flex items-center gap-2 border-b border-border/70 px-4 py-2">
        <span className="min-w-0 flex-1 truncate text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Event
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Close (Esc)"
          aria-label="Close event details"
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X aria-hidden className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <DetailBoundary key={key}>
          <DetailBody snapshot={snapshot} occurrence={occurrence} event={event} />
        </DetailBoundary>
      </div>
    </aside>
  );
}
